import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SeverityBadge } from "@/components/common/SeverityBadge";
import { FileText, Printer, Skull, Bug, Server, ShieldCheck, Target, Download } from "lucide-react";
import { StatCard } from "@/components/common/StatCard";
import { api } from "@/lib/api";
import { downloadCsv, VULN_COLUMNS } from "@/lib/exportCsv";
import { downloadVulnPdf } from "@/lib/exportPdf";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [vulns, setVulns] = useState([]);
  const { user } = useAuth();
  const canExport = user?.plan === "pro";

  const exportCsv = () => {
    if (!canExport) { toast.message("Export réservé au Pro", { description: "Passe à la formule Pro pour exporter." }); return; }
    downloadCsv(`rapport-vulns-${new Date().toISOString().slice(0,10)}.csv`, vulns, VULN_COLUMNS);
  };

  const exportPdf = () => {
    if (!canExport) { toast.message("Export réservé au Pro", { description: "Passe à la formule Pro pour exporter en PDF." }); return; }
    downloadVulnPdf(`rapport-crimson-${new Date().toISOString().slice(0,10)}.pdf`, { stats, scans, vulns });
    toast.success("Rapport PDF généré");
  };

  useEffect(() => {
    api.getStats().then(setStats).catch(() => {});
    api.listScans().then(setScans).catch(() => {});
    api.getVulnerabilities().then(setVulns).catch(() => {});
  }, []);

  const criticals = vulns.filter(v => v.severity === "critical" || v.severity === "high");
  const recommendations = Array.from(new Set(criticals.slice(0, 8).map(v =>
    `Corriger ${v.id} sur ${v.host}:${v.port} — mettre à jour ${v.service || "le service"}.`
  )));

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1400px] mx-auto space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 06 · SECURITY REPORT</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><FileText className="h-8 w-8 text-primary"/> Rapport de sécurité</h1>
          <p className="text-muted-foreground mt-1 text-sm">Synthèse défensive générée à partir de tes scans réels.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={vulns.length === 0} className="font-mono text-xs gap-2" data-testid="report-export-csv-btn"><Download className="h-3.5 w-3.5"/>EXPORT CSV{!canExport && <span className="text-primary">(PRO)</span>}</Button>
          <Button variant="outline" onClick={exportPdf} disabled={vulns.length === 0} className="font-mono text-xs gap-2" data-testid="report-export-pdf-btn"><FileText className="h-3.5 w-3.5"/>EXPORT PDF{!canExport && <span className="text-primary">(PRO)</span>}</Button>
          <Button variant="outline" onClick={() => window.print()} className="font-mono text-xs gap-2"><Printer className="h-3.5 w-3.5"/>IMPRIMER</Button>
        </div>
      </div>

      <Card className="bg-card/60 border-border p-6">
        <div className="font-mono text-[10px] tracking-widest text-primary mb-2">// SYNTHÈSE</div>
        <h2 className="text-2xl font-bold">
          {stats?.vulns ? `${stats.vulns} vulnérabilité(s) détectée(s) dont ${stats.critical} critique(s).` : "Aucune vulnérabilité détectée pour l'instant."}
        </h2>
        <p className="text-muted-foreground mt-3 leading-relaxed max-w-3xl">
          {stats?.scans
            ? `Au cours de ${stats.scans} scan(s) défensif(s), ${stats.hosts} hôte(s) ont été analysés. Priorise la correction des failles critiques et élevées ci-dessous.`
            : "Lance un scan depuis le module Recon & Scan pour générer ce rapport."}
        </p>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Scans" value={stats?.scans ?? 0} icon={Target} tone="info"/>
        <StatCard label="Hôtes analysés" value={stats?.hosts ?? 0} icon={Server}/>
        <StatCard label="Critiques / élevées" value={criticals.length} icon={Skull} tone="danger"/>
        <StatCard label="Total failles" value={stats?.vulns ?? 0} icon={Bug} tone="warning"/>
      </div>

      <Card className="bg-card/60 border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-1">// FAILLES PRIORITAIRES</div>
          <h3 className="font-semibold">À corriger en priorité</h3>
        </div>
        <div className="divide-y divide-border">
          {criticals.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground font-mono text-sm">// aucune faille critique ou élevée</div>}
          {criticals.map((v, idx) => (
            <div key={`${v.id}-${idx}`} className="px-5 py-4 flex items-start gap-4">
              <div className="font-mono text-2xl font-bold text-primary/40 w-10 shrink-0">{String(idx+1).padStart(2,"0")}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-primary">{v.id}</span>
                  <SeverityBadge level={v.severity}/>
                  <span className="font-mono text-[10px] text-warning">CVSS {Number(v.cvss || 0).toFixed(1)}</span>
                </div>
                <div className="font-semibold text-sm mt-1">{v.title}</div>
                <div className="font-mono text-xs text-muted-foreground mt-1">{v.host}:{v.port} · {(v.service || "").toUpperCase()} · {v.source}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-card/60 border-border p-6">
          <div className="font-mono text-[10px] tracking-widest text-success mb-2">// RECOMMANDATIONS</div>
          <h3 className="font-semibold text-lg">Plan de remédiation</h3>
          <ul className="mt-4 space-y-3 text-sm">
            {recommendations.length === 0 && <li className="text-muted-foreground">Aucune action prioritaire.</li>}
            {recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="font-mono text-success text-xs mt-0.5">{String(i+1).padStart(2,"0")}</span>
                <span className="text-muted-foreground leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="bg-card/60 border-border p-6">
          <div className="font-mono text-[10px] tracking-widest text-primary mb-2">// HISTORIQUE DES SCANS</div>
          <h3 className="font-semibold text-lg">Opérations</h3>
          <ol className="mt-4 space-y-3 text-sm">
            {scans.length === 0 && <li className="text-muted-foreground">Aucun scan.</li>}
            {scans.slice(0, 8).map((s) => (
              <li key={s.id} className="flex gap-3">
                <span className="font-mono text-primary text-xs w-24 shrink-0">{new Date(s.started_at).toLocaleDateString()}</span>
                <span className="text-muted-foreground leading-relaxed font-mono">{s.target} · {s.profile} · {s.status}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      <Card className="bg-card/60 border-primary/30 p-5 flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5 text-primary"/></div>
        <div>
          <div className="font-mono text-[10px] tracking-widest text-primary mb-1">// RAPPEL</div>
          <div className="font-semibold text-sm">Scans défensifs autorisés uniquement.</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">Ce rapport ne couvre que des cibles privées ou déclarées dans ta liste blanche. Aucune exploitation offensive n'est réalisée.</p>
        </div>
      </Card>
    </div>
  );
}
